import { type ScaleConfig } from "@visx/scale";
import type { XYChartConfig } from "../../types";
import type { ProcessedSeriesData } from "../../hooks/useChartData";
import {
  BAR_PADDING_INNER,
  LINE_PADDING_INNER,
  Y_DOMAIN_PADDING,
} from "../../constants";

type ScaleType = "band" | "linear" | "time";

// Helper to check if a value is a date
const isDate = (value: any): value is Date =>
  value instanceof Date && !isNaN(value.valueOf());

// Helper to get all unique values for a given axis ('x' or 'y')
const getAllValues = (seriesData: ProcessedSeriesData[], axis: "x" | "y") => {
  return seriesData.flatMap((series) => series.data.map((d) => d[axis]));
};

// Helper to get min and max values from an array
const getExtent = (
  arr: (number | Date)[]
): [number | Date, number | Date] | [undefined, undefined] => {
  if (arr.length === 0) return [undefined, undefined];
  return [
    arr.reduce((min, val) => (val < min ? val : min), arr[0]),
    arr.reduce((max, val) => (val > max ? val : max), arr[0]),
  ];
};

// Helper to get maximum value from an array
const getMax = (arr: number[]): number => {
  if (arr.length === 0) return 0;
  return arr.reduce((max, val) => (val > max ? val : max), arr[0]);
};

// Helper to determine the scale type based on the first valid value
const determineScaleType = (values: any[]): ScaleType => {
  const firstValidValue = values.find((v) => v !== null && v !== undefined);
  if (firstValidValue === undefined) return "linear"; // Default
  if (typeof firstValidValue === "string") return "band";
  if (typeof firstValidValue === "number") return "linear";
  if (isDate(firstValidValue)) return "time";
  return "linear"; // Default fallback
};

// Calculate the maximum sum for stacked bars at each x-value
const calculateMaxStackSum = (
  seriesData: ProcessedSeriesData[],
  config: XYChartConfig
): number => {
  const stackableSeriesKeys = config.series
    .filter((s) => (s.type as any) === "stacked-bar")
    .map((s) => s.key);

  if (stackableSeriesKeys.length === 0) return 0;

  const sumsByX = new Map<any, number>();

  seriesData
    .filter((s) => stackableSeriesKeys.includes(s.key))
    .forEach((series) => {
      series.data.forEach((datum) => {
        if (typeof datum.y === "number") {
          // Only stack numbers
          sumsByX.set(datum.x, (sumsByX.get(datum.x) || 0) + datum.y);
        }
      });
    });

  return getMax(Array.from(sumsByX.values()));
};

export const configureScales = (
  config: XYChartConfig,
  seriesData: ProcessedSeriesData[],
  width: number,
  height: number
) => {
  // Default scales for empty or invalid data
  if (
    !seriesData ||
    seriesData.length === 0 ||
    !seriesData.some((s) => s.data.length > 0)
  ) {
    return {
      xScale: {
        type: "band",
        domain: [],
        paddingInner: 0.1,
      } as ScaleConfig<any>,
      yScale: {
        type: "linear",
        domain: [0, 1],
      } as ScaleConfig<number>,
    };
  }

  // Gather all values for each axis
  const allXValues = getAllValues(seriesData, "x");
  const allYValues = getAllValues(seriesData, "y");

  // Determine the data type for the X axis
  const xDataType = determineScaleType(allXValues);

  // Compute Y domain, handling stacked bars if present
  const isStacked = config.series.some(
    (s) => (s.type as any) === "stacked-bar"
  );
  let yMax = 0;
  let yMin = 0;
  if (isStacked) {
    // For stacked bars, we still want to start at 0
    const maxStackSum = calculateMaxStackSum(seriesData, config);
    const nonStackedYValues = seriesData
      .filter((s) =>
        config.series.find(
          (cs) => cs.key === s.key && (cs.type as any) !== "stacked-bar"
        )
      )
      .flatMap((s) => s.data.map((d) => d.y))
      .filter((v) => typeof v === "number") as number[];
    const maxNonStackedY = getMax(nonStackedYValues);
    const minNonStackedY = Math.min(
      ...(nonStackedYValues.length ? nonStackedYValues : [0])
    );
    yMax = Math.max(maxStackSum, maxNonStackedY);
    yMin = minNonStackedY;
  } else {
    // For non-stacked charts, use the actual data range
    const numericY = allYValues.filter(
      (v) => typeof v === "number"
    ) as number[];
    if (numericY.length > 0) {
      yMax = Math.max(...numericY);
      yMin = Math.min(...numericY);
    } else {
      // Fallback if no numeric values
      yMax = 100;
      yMin = 0;
    }
  }

  // Update the Y domain padding calculation
  const yRange = yMax - yMin;
  const pad = yRange * Y_DOMAIN_PADDING;
  yMax += pad;

  // Check if there are any bar series (regular or stacked)
  const hasBarSeries = config.series.some(
    (s) => s.type === "bar" || s.type === "stacked-bar"
  );

  // If all data is non-negative, start at 0
  if (yMin >= 0) {
    // For bar charts, always start at 0
    if (hasBarSeries) {
      yMin = 0;
    }
    // For other chart types, if the minimum value is high above zero (> 20% of the max value),
    // start slightly below the minimum to avoid squished chart appearance
    else if (yMin > yMax * 0.2) {
      yMin = yMin - yRange * 0.1; // Start 10% below the actual minimum
    } else {
      yMin = 0; // Force exactly 0 for all chart types with non-negative values
    }
  } else {
    // For bar charts with negative values, we still need to show the full bars
    if (hasBarSeries) {
      // Just ensure we have the minimum needed (no extra padding)
      yMin = Math.min(0, yMin);
    } else {
      // Only add padding below zero if there are negative values
      yMin -= pad;
    }
  }

  // Update the paddingInner calculation
  const paddingInner = config.series.some(
    (s) => (s.type as any) === "bar" || (s.type as any) === "stacked-bar"
  )
    ? BAR_PADDING_INNER
    : LINE_PADDING_INNER;

  // Build the X scale based on type
  let xScale: ScaleConfig<any>;
  switch (xDataType) {
    case "band": {
      const domain = [...new Set(allXValues.filter((v) => v != null))].sort();
      xScale = {
        type: "band",
        domain,
        paddingInner,
      };
      break;
    }
    case "time": {
      const [d0, d1] = getExtent(allXValues.filter(isDate) as Date[]);
      const domain: [Date, Date] =
        d0 && d1 ? ([d0, d1] as [Date, Date]) : [new Date(), new Date()];
      xScale = {
        type: "time",
        domain,
      };
      break;
    }
    case "linear":
    default: {
      // Use custom domain if provided, otherwise calculate from data
      let domain: [number, number];
      if (config.xAxisDomain) {
        domain = config.xAxisDomain;
      } else {
        const [n0, n1] = getExtent(
          allXValues.filter((v) => typeof v === "number") as number[]
        ) as [number, number];
        domain = n0 != null && n1 != null ? ([n0, n1] as [number, number]) : [0, 1];
      }
      xScale = {
        type: "linear",
        domain,
        zero: false,
      };
      break;
    }
  }

  // Build the Y scale as a linear scale
  let yDomain: [number, number];
  if (config.yAxisDomain) {
    // Use custom domain if provided
    yDomain = config.yAxisDomain;
  } else {
    // Use calculated domain
    yDomain = [yMin, yMax];
  }

  const yScale: ScaleConfig<number> = {
    type: "linear",
    domain: yDomain,
    nice: true,
    zero: hasBarSeries || yMin === 0, // Always include zero for bar charts
  };

  return { xScale, yScale };
};
